import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTableColumns, resetTableColumns, saveTableColumns } from '../api/table-columns';

const SAVE_DEBOUNCE_MS = 500;

/**
 * 지점별 테이블 컬럼 설정(표시·순서·왼쪽 고정) — 범용 훅.
 *
 * @param {string} tableKey 화면 식별자. 예: 'member-list'
 * @param {Array<{key:string,label:string,hint?:string,pinned?:boolean}>} defaultColumns 화면이 정의한 컬럼 카탈로그(기본 순서·기본 고정)
 *
 * 서버는 카탈로그를 모르므로 병합은 여기서 한다.
 *  - 저장된 순서를 우선하고, 카탈로그에 새로 생긴 키는 기본값으로 뒤에 붙인다.
 *    컬럼에 insertAfter(키)가 있으면 그 컬럼 바로 뒤에 끼운다 — 이미 끝에 붙어 쓰이던 컬럼 자리를 바꾸지 않으려고 새 컬럼에만 단다.
 *  - 카탈로그에서 사라진 키는 버린다.
 *  - 고정 컬럼은 항상 배열 앞쪽에 몰아둔다(불변식).
 * 변경은 로컬에 즉시 반영하고 500ms 디바운스로 PUT 한다.
 */
const useTableColumns = (tableKey, defaultColumns) => {
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;
  const token = user?.token;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['tableColumns', branchId, tableKey], [branchId, tableKey]);

  const { data, isFetched } = useQuery({
    queryKey,
    queryFn: () => getTableColumns(branchId, tableKey, token),
    enabled: !!branchId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const merged = useMemo(() => mergeColumns(defaultColumns, data?.columns), [defaultColumns, data?.columns]);

  // 로컬 편집본. 서버 응답이 바뀌면(다른 계정이 저장 등) 다시 맞춘다.
  const [columns, setColumnsState] = useState(merged);
  useEffect(() => {
    setColumnsState(merged);
  }, [merged]);

  const saveTimer = useRef(null);
  const scheduleSave = useCallback(
    next => {
      if (!branchId || !token) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        saveTimer.current = null;
        try {
          await saveTableColumns(
            branchId,
            tableKey,
            next.map(({ key, visible, pinned }) => ({ key, visible, pinned })),
            token,
          );
          queryClient.setQueryData(queryKey, prev => ({ ...(prev || {}), branchId, tableKey, columns: next }));
        } catch (error) {
          console.error('table column setting save failed', error);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [branchId, tableKey, token, queryClient, queryKey],
  );
  useEffect(() => () => saveTimer.current && clearTimeout(saveTimer.current), []);

  const setColumns = useCallback(
    updater => {
      setColumnsState(prev => {
        const next = normalize(typeof updater === 'function' ? updater(prev) : updater);
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const toggleVisible = useCallback(
    key => setColumns(prev => prev.map(c => (c.key === key ? { ...c, visible: !c.visible } : c))),
    [setColumns],
  );

  /** fromKey 컬럼을 toKey 컬럼 자리로 옮긴다. 고정 여부는 도착지 컬럼을 따라간다(그룹 간 이동 = 고정/해제). */
  const moveColumn = useCallback(
    (fromKey, toKey, placeAfter = false) =>
      setColumns(prev => {
        if (fromKey === toKey) return prev;
        const from = prev.find(c => c.key === fromKey);
        const target = prev.find(c => c.key === toKey);
        if (!from || !target) return prev;
        const without = prev.filter(c => c.key !== fromKey);
        const idx = without.findIndex(c => c.key === toKey) + (placeAfter ? 1 : 0);
        without.splice(idx, 0, { ...from, pinned: target.pinned });
        return without;
      }),
    [setColumns],
  );

  /** 컬럼을 고정 그룹의 끝 / 일반 그룹의 끝으로 보낸다 (빈 그룹에 떨어뜨릴 때). */
  const setPinned = useCallback(
    (key, pinned) =>
      setColumns(prev => {
        const target = prev.find(c => c.key === key);
        if (!target || target.pinned === pinned) return prev;
        const without = prev.filter(c => c.key !== key);
        const moved = { ...target, pinned };
        return pinned ? [...without.filter(c => c.pinned), moved, ...without.filter(c => !c.pinned)] : [...without, moved];
      }),
    [setColumns],
  );

  const reset = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const defaults = mergeColumns(defaultColumns, []);
    setColumnsState(defaults);
    if (!branchId || !token) return;
    try {
      await resetTableColumns(branchId, tableKey, token);
      queryClient.setQueryData(queryKey, { branchId, tableKey, columns: [] });
    } catch (error) {
      console.error('table column setting reset failed', error);
    }
  }, [defaultColumns, branchId, tableKey, token, queryClient, queryKey]);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  return { columns, visibleColumns, loaded: isFetched || !branchId, toggleVisible, moveColumn, setPinned, reset };
};

/** 고정 컬럼을 앞으로 모은다. 상대 순서는 유지. */
const normalize = list => [...list.filter(c => c.pinned), ...list.filter(c => !c.pinned)];

const mergeColumns = (defaults, saved) => {
  const catalog = new Map(defaults.map(c => [c.key, c]));
  const result = [];
  const seen = new Set();
  (saved || []).forEach(item => {
    const def = catalog.get(item.key);
    if (!def || seen.has(item.key)) return;
    seen.add(item.key);
    result.push({ ...def, visible: item.visible !== false, pinned: !!item.pinned });
  });
  defaults.forEach(def => {
    if (seen.has(def.key)) return;
    seen.add(def.key);
    const column = { ...def, visible: def.visible !== false, pinned: !!def.pinned };
    const at = def.insertAfter ? result.findIndex(c => c.key === def.insertAfter) : -1;
    if (at >= 0) result.splice(at + 1, 0, column);
    else result.push(column);
  });
  return normalize(result);
};

export default useTableColumns;
